import SEO from '../../components/SEO';
import React from 'react';
import { motion } from 'framer-motion';
import { Download, Play, CheckCircle2, Calculator, TrendingUp, Shield, FileText, DollarSign, Globe, Award, Target, Zap, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import ProductCTA from '../../components/ProductCTA';
import PersonaNavigation from '../../components/PersonaNavigation';

const FinanceDirectorPage = () => {
  const stats = [
    { value: "100%", label: "MTD Compliant" },
    { value: "Real-time", label: "Cash Flow" },
    { value: "Multi-Currency", label: "Global Ready" },
    { value: "Days", label: "Month-End Close" }
  ];

  const benefits = [
    { icon: Calculator, title: "Complete Financial Control", strapline: "Automate and streamline", desc: "Automated journal posting, budgeting, and real-time financial statements available instantly." },
    { icon: Shield, title: "Making Tax Digital Ready", strapline: "HMRC approved", desc: "Full MTD compliance for VAT with automated submissions and audit trails." },
    { icon: Globe, title: "Multi-Currency", strapline: "Global operations", desc: "Handle multiple currencies with automatic exchange rates and consolidated reporting." },
    { icon: TrendingUp, title: "Real-Time Cash Flow", strapline: "Never be surprised", desc: "Live cash flow visibility with automated forecasting and working capital optimisation." },
    { icon: FileText, title: "Instant Reporting", strapline: "Management accounts", desc: "Generate P&L, balance sheets instantly with Crystal Reports integration." },
    { icon: DollarSign, title: "Cost Control", strapline: "True margins", desc: "Track costs by department, project, or product with real-time profitability analysis." }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="SAP Business One for Finance Directors | Financial Control & Compliance"
        description="Financial excellence with SAP B1. MTD compliant, real-time reporting, cash flow visibility, automated compliance. Perfect for UK Finance Directors and CFOs."
        keywords="finance director ERP, CFO software, MTD compliance, financial reporting, SAP B1 finance"
        canonical="https://www.ascarii.com/sap-business-one/for/finance-directors"
      />

      <section className="relative min-h-[700px] flex items-center overflow-hidden bg-gradient-to-br from-green-700 via-emerald-800 to-green-900">
        <div className="absolute inset-0 z-0">
          <img src="https://images.pexels.com/photos/8927463/pexels-photo-8927463.jpeg?w=1920&auto=compress" alt="Finance" className="w-full h-full object-cover opacity-30" loading="eager" />
        </div>
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full text-sm font-semibold mb-6 text-white">For Finance Directors</div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight text-white">Financial Excellence, Total Control</h1>
              <p className="text-xl md:text-2xl text-green-100 mb-8 leading-relaxed">Automated MTD compliance, real-time cash flow, and complete financial control for CFOs who demand accuracy.</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-green-600 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Persona Navigation */}
      <PersonaNavigation currentPersona="finance-directors" />

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col justify-center">
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4 w-fit">Real-Time Analytics</div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Financial Data and KPIs in Real-Time</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Enhanced analytical functionality enables important financial data and KPIs to be delivered in <strong>real-time</strong>, meaning key decisions can happen quicker to achieve maximum business impact.
              </p>
              <div className="p-6 bg-gradient-to-br from-green-50 to-white rounded-xl border border-green-200">
                <h3 className="font-bold text-gray-900 mb-3">Automation Frees Your Time</h3>
                <p className="text-gray-700">
                  Significant automation of previously manual processes provides greater time to focus on key financial controls such as cash flow and deliver more accurate management reporting.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
                <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=85" alt="Analytics" className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tl from-blue-600/20 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-green-800 via-emerald-900 to-green-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why Finance Directors Choose SAP Business One</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.05 }} viewport={{ once: true }} className="group p-8 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:border-cyan-300 hover:bg-white/20 transition-all">
                <div className="flex items-start gap-6">
                  <div className="w-20 h-20 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-10 h-10 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2">{benefit.title}</h3>
                    <p className="text-lg text-cyan-300 mb-3 font-medium">{benefit.strapline}</p>
                    <p className="text-green-100 text-sm leading-relaxed">{benefit.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-12 border-2 border-green-200">
              <div className="text-center">
                <div className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-6">Real Success</div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Hawthorn Trading</h2>
                <p className="text-lg text-gray-700 italic mb-4">
                  We have much better purchasing power because of the data from SAP Business One. We saved 13,000 pounds in the first quarter just by switching supplier.
                </p>
                <p className="text-sm text-gray-600">- Finance Director</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-100 via-green-50 to-gray-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="lg:col-span-2">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Transform Your Finance Function</h2>
              <p className="text-xl text-gray-600 mb-8">Join CFOs achieving MTD compliance</p>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 flex items-center justify-center">
                    <Shield className="w-7 h-7 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">MTD Compliant</h4>
                    <p className="text-gray-600 text-sm">HMRC approved</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 flex items-center justify-center">
                    <Clock className="w-7 h-7 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Fast Close</h4>
                    <p className="text-gray-600 text-sm">Days not weeks</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 flex items-center justify-center">
                    <Globe className="w-7 h-7 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Global Ready</h4>
                    <p className="text-gray-600 text-sm">Multi-currency</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="lg:col-span-3">
              <ProductCTA productName="SAP Business One for Finance Directors" brandColor="green-600" />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FinanceDirectorPage;
